import { useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
// Vite: Worker als URL einbinden (bereits im Build vorhanden).
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Loader2 } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
// Decoder für JPEG2000/JBIG2-Scans (siehe vite.config.ts, Plugin pdfjs-wasm).
const PDFJS_WASM_URL = `${import.meta.env.BASE_URL}pdfjs-wasm/`;

/**
 * Scrollbarer Mehrseiten-PDF-Viewer. Rendert alle Seiten clientseitig (pdf.js)
 * zu Bildern, gestapelt in einem scrollbaren Container.
 *
 * Hintergrund: Ein <iframe> mit PDF ist auf iOS/vielen mobilen Browsern NICHT
 * scrollbar (man sieht nur Seite 1). Gerenderte Seiten in einem overflow-auto-Div
 * scrollen dagegen auf allen Geräten.
 *
 * `url` ist typischerweise eine Blob-URL (bereits mit Auth geladen).
 */
export function PdfScrollViewer({ url }: { url: string }) {
  const [pages, setPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;
    setPages([]);
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const buf = await (await fetch(url)).arrayBuffer();
        if (cancelled) return;
        // pdf.js detacht den Buffer — Kopie übergeben.
        loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(buf.slice(0)), wasmUrl: PDFJS_WASM_URL });
        const pdf = await loadingTask.promise;
        const out: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;
          const canvas = document.createElement('canvas');
          try {
            const page = await pdf.getPage(i);
            const base = page.getViewport({ scale: 1 });
            // Renderbreite ~1100px → per CSS auf die Containerbreite skaliert (scharf auf Handy).
            const viewport = page.getViewport({ scale: 1100 / base.width });
            canvas.width = Math.ceil(viewport.width);
            canvas.height = Math.ceil(viewport.height);
            await page.render({ canvas, viewport } as any).promise;
            page.cleanup();
            out.push(canvas.toDataURL('image/png'));
          } catch {
            out.push(''); // defekte Seite → Platzhalter, Vorschau lebt weiter
          }
          if (!cancelled) setPages([...out]); // progressiv anzeigen
        }
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) {
          setError('PDF konnte nicht geladen werden.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      try { loadingTask?.destroy(); } catch { /* egal */ }
    };
  }, [url]);

  return (
    <div
      className="w-full h-full overflow-auto overscroll-contain flex flex-col items-center gap-3 p-1 sm:p-2"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {pages.map((src, i) => src
        ? <img key={i} src={src} alt={`Seite ${i + 1}`} className="w-full max-w-[1100px] h-auto rounded bg-white shadow" />
        : <div key={i} className="w-full max-w-[1100px] aspect-[1/1.414] grid place-items-center bg-slate-100 text-slate-400 text-sm rounded">Seite {i + 1} nicht darstellbar</div>
      )}
      {loading && <div className="grid place-items-center text-white py-6"><Loader2 className="w-6 h-6 animate-spin" /></div>}
      {error && <div className="grid place-items-center text-white/90 text-sm py-6">{error}</div>}
    </div>
  );
}
